import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  IconButton
} from '@mui/material';
import MDEditor, { commands } from '@uiw/react-md-editor';
import { updateParagraph, attachContentImageToParagraph } from '../../services/api';
import ImageGeneratorDialog from '../ImageGeneratorDialog';
import ImageIcon from '@mui/icons-material/Image';
import { useProject } from '../../contexts/ProjectContext';

const imageCommand = {
  name: 'image',
  keyCommand: 'image',
  buttonProps: { 'aria-label': 'Insert image' },
  icon: <ImageIcon />,
  execute: (state, api) => {
    // This will be handled by our custom logic
    return;
  },
};

const isDev = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EditParagraphDialog = ({ 
  open, 
  onClose, 
  paragraph, 
  articleId, 
  onSave,
  loading 
}) => {
  const { selectedProject } = useProject();
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [editorSelection, setEditorSelection] = useState(null);

  useEffect(() => {
    if (paragraph) {
      setContent(paragraph.content || '');
      setError(null);
    }
  }, [paragraph]);

  useEffect(() => {
    console.log('Current content:', content);
    console.log('Current paragraph:', paragraph);
  }, [content, paragraph]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await updateParagraph(articleId, paragraph.id, { content });
      onSave();
    } catch (error) {
      console.error('Error updating paragraph:', error);
      setError(error.response?.data?.detail || 'Error updating paragraph');
    } finally {
      setSaving(false);
    }
  };

  const handleImageGenerated = async (image) => {
    try {
      // First, attach the image to paragraph content (not as header)
      await attachContentImageToParagraph(articleId, paragraph.id, { image_id: image.id });
      
      // Then insert the markdown with the full URL
      const imageUrl = image.image_url.startsWith('http') 
        ? image.image_url 
        : `${API_BASE_URL}${image.image_url}`;
        
      const imageMarkdown = `![${image.prompt || 'Image'}](${imageUrl})`;
      
      if (editorSelection) {
        const newContent = 
          content.substring(0, editorSelection.start) +
          imageMarkdown +
          content.substring(editorSelection.end);
        setContent(newContent);
      } else {
        // If no selection, append to the end
        setContent((prevContent) => {
          return prevContent ? `${prevContent}\n\n${imageMarkdown}` : imageMarkdown;
        });
      }
    } catch (error) {
      setError('Error attaching image to paragraph content: ' + error.message);
    }
  };

  const customCommands = [
    commands.bold,
    commands.italic,
    commands.strikethrough,
    commands.hr,
    commands.title,
    commands.divider,
    commands.quote,
    commands.code,
    commands.codeBlock,
    commands.link,
    commands.divider,
    commands.unorderedListCommand,
    commands.orderedListCommand,
    commands.checkedListCommand,
    commands.divider,
    {
      ...imageCommand,
      execute: (state, api) => {
        setEditorSelection({
          start: state.selection.start,
          end: state.selection.end
        });
        setImageDialogOpen(true);
      }
    }
  ];

  // Create a custom preview renderer that adds the API base URL to relative image paths
  const previewOptions = {
    components: {
      img: ({ src, alt }) => {
        const imageUrl = src.startsWith('http') ? src : `${API_BASE_URL}${src}`;
        return <img src={imageUrl} alt={alt} style={{ maxWidth: '100%' }} />;
      }
    }
  };

  if (!paragraph) {
    return null;
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh'
          }
        }}
      >
        <DialogTitle>
          Edit Paragraph: {paragraph?.heading}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <MDEditor
              value={content}
              onChange={(value) => {
                console.log('MDEditor onChange:', value); // Debug log
                setContent(value || '');
              }}
              preview="live"
              height={500}
              commands={customCommands}
              extraCommands={[]}
              previewOptions={previewOptions}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ImageGeneratorDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        articleId={articleId}
        paragraphId={paragraph?.id}
        paragraphContent={content || paragraph?.content || ''}
        paragraphHeading={paragraph?.heading}
        onImageGenerated={(image) => {
          handleImageGenerated(image);
          setImageDialogOpen(false);
        }}
        contentOnly={true}
        projectId={selectedProject?.id}
      />
    </>
  );
};

export default EditParagraphDialog; 